<template>
    <!-- PageBreadcrumb teleports itself to App.vue header -->
    <PageBreadcrumb>
        <template #breadcrumb-label="{ item }">
            <div v-if="item.name === 'topic-details'" class="flex flex-col">
                <span class="font-medium text-foreground truncate" style="max-width: 25rem" :title="topic?.name">
                    {{ topic?.name || 'Loading...' }}
                </span>
            </div>
            <span v-else class="truncate max-w-60" :title="item.label">{{ item.label }}</span>
        </template>
        <template #actions>
            <ActionMenu
                v-can-access.permissions="[$Permissions.WRITE_TOPICS]"
                variant="menu"
                menu-trigger-size="md"
                :show-copy-id="true"
                :show-delete="true"
                :loading="topicLoading || configLoading"
                @copyId="onCopyTopicId(topic.id)"
                @delete="onDeleteTopic('topic', topic.name, topic.id)" />
        </template>
    </PageBreadcrumb>

    <div>
        <!-- Stats Section -->
        <AppLoader :force-loading="topicLoading || configLoading" default-height="60vh" class="min-h-60">
            <div v-if="topic" class="w-full">
                <Tabs :tabs="tabs" :initial-active-tab="currentTab" @tab-change="setCurrentTab">
                    <template #Status>
                        <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 mb-10">
                            <StatCard
                                v-for="stat in stats"
                                :title="stat.name"
                                :value="stat.stat"
                                :icon="stat.icon"
                                :icon-bg-class="stat.colorClass"
                                :loading="topicLoading || configLoading || topicMetricsLoading" />
                        </div>

                        <!-- Search and Filters -->
                        <div class="w-full flex justify-between min-h-[60px]">
                            <div class="flex items-center gap-2">
                                <Button variant="outline" @click="openFilterModal" :disabled="messagesLoading" class="gap-2 shrink-0">
                                    <Filter v-if="!hasSelection" class="h-4 w-4" />
                                    Filter Messages
                                </Button>
                                <LoadingButton
                                    size="md"
                                    class="flex-shrink-0 h-9"
                                    :is-loading="messagesLoading"
                                    :disabled="messagesLoading"
                                    :show-content-on-loading="false"
                                    @click="refreshTopicsMessages">
                                    <RefreshCwIcon class="h-4 w-4 text-primary-foreground dark:text-primary-foreground/70" />
                                </LoadingButton>

                                <TooltipProvider>
                                    <div
                                        v-if="
                                            (topicFilter?.regex && topicFilter?.regex?.pattern && topicFilter?.regex?.pattern?.trim() !== '') ||
                                            (topicFilter?.seek?.seekType && topicFilter?.seek?.seekValue !== null)
                                        "
                                        class="flex items-center">
                                        <Badge variant="outline" class="flex items-center gap-2 px-3 py-1">
                                            <span>Filter applied</span>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <button
                                                        type="button"
                                                        class="inline-flex items-center justify-center rounded-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:hover:bg-accent/80 dark:focus:ring-offset-background transition-colors">
                                                        <Info class="h-4 w-4 text-primary dark:text-primary/80" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" class="max-w-xs">
                                                    <p class="text-sm" v-html="sanitizeHtml(filterTooltipText)"></p>
                                                </TooltipContent>
                                            </Tooltip>
                                            <button
                                                type="button"
                                                @click="removeFilter"
                                                class="inline-flex items-center justify-center rounded-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:hover:bg-accent/80 dark:focus:ring-offset-background transition-colors"
                                                aria-label="Remove filter">
                                                <X class="w-4 h-4" />
                                            </button>
                                        </Badge>
                                    </div>
                                </TooltipProvider>

                                <Badge v-if="kafkaPartitions?.count" variant="outline"> {{ kafkaPartitions.count }} partition(s) </Badge>
                            </div>
                            <div class="flex items-center justify-end gap-5">
                                <Select :model-value="sortingSelected" :disabled="messagesLoading" @update:model-value="onSortingChange">
                                    <SelectTrigger class="w-full sm:w-[180px]">
                                        <SelectValue placeholder="Sort by" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem v-for="option in sortingOptions" :key="option.value" :value="option.value">
                                            {{ option.name }}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <!-- Chart for Topic Data -->
                        <!-- <div class="bg-white p-4 rounded-lg shadow-sm mb-6">
                        <div class="flex gap-2 items-center">
                            <ChevronUpIcon
                                v-if="showChart"
                                class="h-5 w-5 text-muted-foreground cursor-pointer hover:text-foreground"
                                @click="showChart = !showChart" />
                            <ChevronDownIcon
                                v-else
                                class="h-5 w-5 text-muted-foreground cursor-pointer hover:text-foreground"
                                @click="showChart = !showChart" />
                            <h3 v-if="!showChart">{{ chartOptions.title.text }}</h3>
                        </div>
                        <HighChart v-if="showChart" :chart-options="chartOptions" />
                    </div> -->

                        <TopicMessagesTable
                            :messages="messages"
                            :page-size="messagesPageSize"
                            :unselect-all="!selectedMessage"
                            :page="messagesPage"
                            :sort="sortingSelected"
                            @select-message="selectMessage"
                            @page-change="({ page, direction }) => fetchTopicsMessages(page, null, direction, null)"
                            @page-size-change="onMessagesPerPageChange"
                            :loading="messagesLoading"
                            :total-messages="topicsStore.messagesPageTotal" />

                        <!-- Side Popup for Selected Message -->
                        <TopicMessageDetailsDrawer :message="selectedMessage" @close="selectMessage(null)" />

                        <TopicMessagesFilterModal
                            v-if="showFilterModal"
                            :isOpen="showFilterModal"
                            :initialFilter="topicFilter"
                            @apply="applyFilter"
                            @update:isOpen="
                                (val) => {
                                    showFilterModal = val;
                                }
                            " />

                        <!-- Tab Navigation -->
                        <!-- <Tabs :tabs="tabs" @tab-change="setCurrentTab">

               <template #Schema>
                    <TopicColumnSchema :columns="topic?.columns" @hash="onChangeHashColumnState" @exclude="onChangeExcludeColumnState" />
                </template>
            </Tabs> -->
                    </template>
                    <template #Metadata>
                        <TopicMetadataTab :topic="topic" :format-kafka-config="formatKafkaConfig" />
                    </template>
                    <template #Settings>
                        <div class="mt-5">
                            <Card class="w-full mx-auto">
                                <CardContent class="p-6">
                                    <div>
                                        <Label class="block mb-2"> Partition Count (Can't be lower than the current count) </Label>
                                        <div class="space-y-3">
                                            <Slider
                                                v-model="partitionCountArray"
                                                :min="kafkaPartitions?.count || 1"
                                                :max="100"
                                                :step="1"
                                                :default-value="[newPartitionCount || 1]" />
                                            <div class="text-center">
                                                <span class="text-sm font-medium"> Current Value: {{ newPartitionCount }} </span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Warning Message -->
                                    <Alert class="mt-4">
                                        <AlertTriangle class="h-4 w-4" />
                                        <AlertTitle>WARNING</AlertTitle>
                                        <AlertDescription>
                                            <p class="mb-3">
                                                <strong
                                                    >Increasing partitions will cause messages to be read out of order and may reorganize destination
                                                    tables.</strong
                                                >
                                            </p>

                                            <div class="mb-3 p-3 bg-destructive/10 rounded-md">
                                                <p class="font-semibold mb-2 text-destructive">Safe Partition Increase Procedure:</p>
                                                <ol class="list-decimal list-inside space-y-1 text-sm text-destructive">
                                                    <li>Stop sources/producers</li>
                                                    <li>Wait for consumer lag to drop to 0</li>
                                                    <li>Stop destinations streaming this topic</li>
                                                    <li>Truncate destination table (Optional - depending on destination)</li>
                                                    <li>Increase topic partitions</li>
                                                    <li>Start sources/producers</li>
                                                    <li>Trigger snapshot (Optional - depending on destination)</li>
                                                    <li>Start destinations</li>
                                                </ol>
                                            </div>

                                            <p class="text-sm">
                                                <strong>ClickHouse</strong> (via
                                                <code class="text-xs bg-muted px-1.5 py-0.5 rounded">ReplacingMergeTree</code> background merging) and
                                                <strong>Snowflake</strong> (using Dynamic Tables/dbt, sorted by
                                                <code class="text-xs bg-muted px-1.5 py-0.5 rounded"
                                                    >_streamkap_ts_ms DESC, _streamkap_offset DESC</code
                                                >) destinations handle out-of-order records safely through post-ingestion de-duplication. Other
                                                destinations require the full procedure above.
                                            </p>
                                        </AlertDescription>
                                    </Alert>
                                </CardContent>
                            </Card>
                        </div>
                    </template>
                </Tabs>
            </div>
        </AppLoader>

        <!-- Footer will teleport to App.vue footer slot -->
        <div v-if="topic" v-can-access.permissions="[$Permissions.WRITE_TOPICS]">
            <AppFooter>
                <template #right>
                    <ActionMenu
                        variant="row"
                        :show-save="true"
                        :disable-save="!newPartitionCount || (kafkaPartitions?.count || 1) >= newPartitionCount"
                        :loading="topicLoading || configLoading || saveLoading"
                        @save="saveTopic"
                        @discard="onDiscard" />
                </template>
            </AppFooter>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, isRef, watch } from 'vue';
import { AlertCircle, ChevronsLeft, Scale, List, Info, X, Filter, AlertTriangle, RefreshCwIcon } from 'lucide-vue-next';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import AppFooter from '../../components/AppFooter.vue';
import StatCard from '../../components/global/StatCard.vue';
import TopicMessagesTable from '../../components/Topics/TopicMessagesTable.vue';
import TopicMessageDetailsDrawer from '../../components/Topics/TopicMessageDetailsDrawer.vue';
import TopicMetadataTab from '../../components/Topics/TopicMetadataTab.vue';
import { useRoute, useRouter } from 'vue-router';
import { useTopicsStore } from '../../stores/topics.store';
import TopicMessagesFilterModal from '@/components/global/modals/TopicMessagesFilterModal.vue';
import ActionMenu from '../../components/ActionMenu.vue';
import Tabs from '@/components/global/Tabs.vue';
import { formatter, getMetricFormattingInfo, sanitizeHtml } from '../../utils/utils';
import _ from 'lodash';
import { useLoaderStore } from '@/stores/loader.store';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AppLoader from '@/components/AppLoader.vue';
import { useDeleteConfirmation, useConfirmDialog } from '@/composables/useDialog';
import { toast } from '@/composables/useToast';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS } from '@/utils/constants';

const route = useRoute();
const router = useRouter();

const topicsStore = useTopicsStore();

const { registerFilter } = useUrlFilters({
    defaults: {
        tab: FILTER_DEFAULTS.TABS.STATUS
    }
});
const defaultTopic = ref({});

const messagesPageSize = ref(10);
const messagesPage = ref(0);

const messages = ref(null);
const topicLoading = ref(false);
const messagesLoading = ref(false);
const configLoading = ref(false);
const openSortDropdown = ref(false);
const sortingSelected = ref('asc');
const lastOffset = ref(null);
const firstEverOffset = ref(null);
const paginationDirection = ref(null);
const showFilterModal = ref(false);
const saveLoading = ref(false);

const topicFilter = ref({
    regex: {
        fields: [], // or prepopulate with default fields, e.g. ['key']
        pattern: ''
    },
    seek: {
        seekType: 'offset', // valid values: 'none', 'offset', 'timestamp'
        seekValue: null
    }
});

const newPartitionCount = ref(null);

// Computed property to handle shadcn-vue Slider array format
const partitionCountArray = computed({
    get: () => [newPartitionCount.value || 0],
    set: (value) => {
        newPartitionCount.value = value[0];
    }
});

const topic = computed(() => {
    // Priority 1: Use defaultTopic if available (it has the correct ID from API response)
    if (defaultTopic.value && defaultTopic.value.id) {
        // Try to find the topic with updated metrics in the store using exact match
        const storeTopicWithMetrics = topicsStore.all?.find((t) => t.id === defaultTopic.value.id);
        if (storeTopicWithMetrics && storeTopicWithMetrics.metrics) {
            return storeTopicWithMetrics;
        }
        return defaultTopic.value;
    }

    // Priority 2: Fallback to exact match by route param if defaultTopic not available
    return topicsStore.all?.find((t) => t.id === route.params.topic_id) || null;
});

const sortingOptions = ref([
    {
        name: 'Newest First',
        value: 'desc'
    },
    {
        name: 'Oldest First',
        value: 'asc'
    }
]);

const loaderStore = useLoaderStore();

const getFormattedValue = (metricKey, value) => {
    const metricInfo = getMetricFormattingInfo(metricKey, topicsStore.configs?.metrics);

    return formatter(value, metricInfo || {}, (value) => _.isNil(value));
};

const topicMetricsLoading = computed(() => loaderStore.isLoading('metrics.topics.fetch.table'));

// Kafka-specific computed properties
const kafkaPartitions = computed(() => topic.value?.kafka?.partitions);

// Format Kafka configuration values with appropriate units
const formatKafkaConfig = (key, value) => {
    if (!value || value === '-1' || value === 'null') return '-';

    switch (key) {
        case 'retention_ms':
        case 'delete_retention_ms':
        case 'segment_ms':
            return formatter(parseInt(value), { unit: 'ms' }).display;
        case 'retention_bytes':
        case 'max_message_bytes':
        case 'segment_bytes':
            return parseInt(value) === -1 ? 'unlimited' : formatter(parseInt(value), { unit: 'bytes' }).display;
        default:
            return value || '-';
    }
};

let tabs = [
    { name: 'Status', href: '#', current: true },
    { name: 'Metadata', href: '#', current: false },
    { name: 'Settings', href: '#', current: false }
];

const currentTab = registerFilter('tab', FILTER_DEFAULTS.TABS.STATUS);

const setCurrentTab = (str) => {
    currentTab.value = str;
};

const toggleDropdown = () => {
    openSortDropdown.value = !openSortDropdown.value;
};

const chartOptions = ref({
    chart: {
        type: 'column',
        height: '300px'
    },
    title: {
        text: 'Message Volume Over Time'
    },
    xAxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    },
    yAxis: {
        min: 0,
        title: {
            text: 'Volume'
        }
    },
    tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="padding:0"><b>{point.y:.1f} messages</b></td></tr>',
        footerFormat: '</table>',
        shared: true,
        useHTML: true
    },
    plotOptions: {
        column: {
            pointPadding: 0.2,
            borderWidth: 0,
            dataLabels: {
                enabled: true
            }
        }
    },
    series: [
        {
            name: 'Messages',
            data: [49.9, 71.5, 106.4, 129.2, 144.0, 176.0, 135.6, 148.5, 216.4, 194.1, 95.6, 54.4], // Mock data
            borderRadiusTopLeft: '15%',
            borderRadiusTopRight: '15%',
            color: '#958cee'
        }
    ]
});

const stats = computed(() => {
    const [volume, errors, eventsSent, written] = ['byteTotal', 'recordErrorTotal', 'recordSendTotal', 'recordWrittenTotal'].map((metricKey) => {
        return {
            metric: metricKey,
            displayValue: getFormattedValue(metricKey, topic.value?.metrics?.[metricKey]).display
        };
    });

    return [
        {
            id: 3,
            name: 'Written',
            stat: written.displayValue,
            icon: List,
            colorClass: 'bg-primary'
        },
        {
            id: 0,
            name: 'Volume',
            stat: volume.displayValue,
            icon: Scale,
            colorClass: 'bg-chart-4'
        },
        {
            id: 1,
            name: 'Errors',
            stat: errors.displayValue,
            icon: AlertCircle,
            colorClass: 'bg-destructive'
        },
        {
            id: 2,
            name: 'Events',
            stat: eventsSent.displayValue,
            icon: ChevronsLeft,
            colorClass: 'bg-chart-1'
        }
    ];
});

function onMessagesPerPageChange(newSize) {
    messagesPage.value = 0;
    fetchTopicsMessages(messagesPage.value, newSize, null, null);
}

function onSortingChange(newSort) {
    sortingSelected.value = newSort;
    messagesPage.value = 0;
    lastOffset.value = null;
    paginationDirection.value = null;
    fetchTopicsMessages(null, null, null, newSort);
}

async function fetchTopicsMessages(newPage = null, newPageSize = null, direction = null, sort = null) {
    messagesLoading.value = true;
    try {
        messagesPage.value = _.isNil(newPage) ? messagesPage.value : newPage;
        messagesPageSize.value = _.isNil(newPageSize) ? messagesPageSize.value : newPageSize;
        sortingSelected.value = _.isNil(sort) ? sortingSelected.value || 'asc' : sort;
        paginationDirection.value = _.isNil(direction) ? paginationDirection.value : direction;

        const query = {
            page_size: messagesPageSize.value,
            page: messagesPage.value,
            sort: sortingSelected.value,
            direction: paginationDirection.value
        };

        // If regex filter is applied (non-empty pattern), include regex parameters.
        if (topicFilter.value.regex && topicFilter.value.regex?.pattern && topicFilter.value.regex?.pattern?.trim() !== '') {
            query.regex = topicFilter.value.regex.pattern;
            query.regex_search_fields = topicFilter.value.regex.fields;
            if (topicFilter.value.seek && topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
                query.seek_type = topicFilter.value.seek.seekType;
                query.seek_value = topicFilter.value.seek.seekValue;
            }
        }
        // Otherwise, if only a seek filter is provided.
        else if (topicFilter.value?.seek && topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
            query.seek_type = topicFilter.value.seek.seekType;
            query.seek_value = topicFilter.value.seek.seekValue;
        }

        const topicId = topic.value?.id || defaultTopic.value?.id || route.params.topic_id;
        const res = await topicsStore.fetchTopicMessages(topicId, query);
        messages.value = res || [];
        messagesLoading.value = false;
        return res;
    } catch (error) {
        messages.value = [];
        messagesLoading.value = false;
        console.error(error);
    }
}

const refreshTopicsMessages = async () => {
    messagesPage.value = 0; // Reset to first page
    lastOffset.value = null;
    paginationDirection.value = null;
    await fetchTopicsMessages();
};

const openFilterModal = () => {
    showFilterModal.value = true;
};

const applyFilter = (filterData) => {
    topicFilter.value = filterData;
    showFilterModal.value = false;
    messagesPage.value = 0;
    lastOffset.value = null;
    firstEverOffset.value = null;
    paginationDirection.value = null;

    fetchTopicsMessages();
};

const selectedMessage = ref(null);

const searchQuery = ref('');
const pageSize = ref(0);

const selectMessage = (message) => {
    selectedMessage.value = isRef(message) ? message?.value : message;
};

const removeFilter = () => {
    topicFilter.value = {
        regex: { fields: [], pattern: '' },
        seek: { seekType: null, seekValue: null }
    };

    messagesPage.value = 0;
    lastOffset.value = null;
    firstEverOffset.value = null;
    paginationDirection.value = null;

    fetchTopicsMessages();
};

watch(
    () => kafkaPartitions.value?.count,
    (newValue) => {
        newPartitionCount.value = Number(newValue || 1);
    },
    { immediate: true }
);

onMounted(async () => {
    topicLoading.value = true;
    messagesLoading.value = true;
    configLoading.value = true;
    await Promise.all([
        topicsStore.getById(route.params.topic_id).then(
            (res) => {
                defaultTopic.value = _.cloneDeep(res);

                topicLoading.value = false;

                return res;
            },
            (err) => {
                topicLoading.value = false;
                console.error(err);
            }
        ),
        fetchTopicsMessages(),
        topicsStore.fetchTopicConfigs().then(
            (res) => {
                configLoading.value = false;
                return res;
            },
            (err) => {
                console.error(err);
            }
        )
    ]);
});

// const canSave = computed(() => {
//     return !_.isEqual(topic.value, defaultTopic.value);
// });

function canShowCancelSnapshot() {
    /* TBD */
    return false;
}

async function onSnapshot() {
    /* TBD */
}

async function onCancelSnapshot() {
    /* TBD */
}

async function onChangeHashColumnState(column) {
    /* TBD */
}

async function onChangeExcludeColumnState(column) {
    /* TBD */
}

async function saveTopic() {
    try {
        await useConfirmDialog({
            type: 'save',
            title: 'Update Topic',
            message: `Are you sure you want to update the topic <strong>"${topic.value.name}"</strong>? This will change the partition count.`,
            confirmText: 'Update Topic',
            requiresInput: false,
            onConfirm: async () => {
                // Set loading state AFTER confirmation
                saveLoading.value = true;

                try {
                    const topicId = topic.value?.id || defaultTopic.value?.id || route.params.topic_id;
                    await topicsStore.update(topicId, {
                        topic_db_ids: topic.value.entity.topic_db_ids,
                        payload: {
                            partition_count: newPartitionCount.value
                        }
                    });

                    // Refetch topic data to get updated information
                    await topicsStore.getById(topicId);
                } finally {
                    saveLoading.value = false;
                }
            }
        });
    } catch (error) {
        if (error.message) {
            toast.error('Failed to update the topic.');
            console.error('Update failed:', error);
        }
    } finally {
        // Loading state is now handled within onConfirm callback
    }
}

async function onDiscard() {
    newPartitionCount.value = Number(kafkaPartitions.value?.count || 1);
}

/**
 * Copy topic ID to clipboard
 */
async function onCopyTopicId(topicId) {
    try {
        await navigator.clipboard.writeText(topicId);
        // Show success toast
        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    } catch (error) {
        console.error('Failed to copy topic ID:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = topicId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    }
}

/**
 * Prompt and delete this topic, then navigate back to topics list.
 */
async function onDeleteTopic(entity, name, id) {
    try {
        await useDeleteConfirmation({
            entity,
            name,
            title: `Delete ${entity}`,
            message: `Are you sure you want to delete the ${entity} <strong>"${name}"</strong>? This action cannot be undone and you will be redirected to the topics page.`,
            onConfirm: async () => {
                await topicsStore.delete(id);
                router.push({ name: 'topics' });
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    }
}

const hasSelection = computed(() => {
    const regexExists = topicFilter.value.regex?.pattern && topicFilter.value.regex?.pattern?.trim() !== '';
    const seekExists = topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null;
    return regexExists || seekExists;
});

const filterTooltipText = computed(() => {
    let message = '';
    const regexExists = topicFilter.value.regex?.pattern && topicFilter.value.regex?.pattern?.trim() !== '';
    // dummy
    if (regexExists) {
        message += `<b>Regex (${topicFilter.value.regex.fields?.join(', ')}):</b> ${topicFilter.value.regex.pattern}`;
    }

    if (topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
        message += `${regexExists ? '<br>' : ''}<b>Seek (${topicFilter.value.seek.seekType}):</b> ${topicFilter.value.seek.seekType === 'timestamp' ? new Date(topicFilter.value.seek.seekValue).toUTCString() : topicFilter.value.seek.seekValue}`;
    }

    return message;
});
</script>

<style scoped>
/* Tailwind CSS styling */
</style>
